import { FundDataBasic, TargetPopulation } from "../interfaces/DTO/FundDataBasic";
import { DisplayPreferences } from "../components/GemelData/types";
import { ProductType } from "../interfaces/DTO/mislaka_xml_models";

// ברירת מחדל לפרופרטיז של GemelInvestigationPanel
export const getDefaultDisplayPreferences = (): DisplayPreferences => {
  return {
    filterPopulation: true,
    colorBy: 'default',
    pinnedItems: {
      subSpecializations: ["מניות", "כללי"]
    }
  };
};

// רשימת סוגי מוצרים נתמכים (לפי נפוצות)
export const supportedProductTypes = [
  ProductType.EDUCATION_FUND,           // קרן השתלמות
  ProductType.INVESTMENT_PROVIDENT_FUND, // קופת גמל להשקעה
  ProductType.PURE_SAVINGS_POLICY,      // ביטוח חסכון טהור
  ProductType.PROVIDENT_FUND,           // קופת גמל לתגמולים
  ProductType.PENSION_FUND,             // קרן פנסיה
  ProductType.LIFE_INSURANCE_SAVINGS,   // ביטוח חיים חסכון
  ProductType.CHILD_SAVINGS,            // ילדים וקטינים
];

// רשימת כל buckets של חשיפה למניות (6 טווחים מפורטים)
// max לא כלול בטווח (min <= value < max)
export const stockExposureBuckets = [
  { label: "0-10", min: 0, max: 10 },    // 0-9%
  { label: "10-30", min: 10, max: 30 },  // 10-29%
  { label: "30-60", min: 30, max: 60 },  // 30-59%
  { label: "60-75", min: 60, max: 75 },  // 60-74%
  { label: "75-90", min: 75, max: 90 },  // 75-89%
  { label: "90-100", min: 90, max: 130 }, // 90-100% (max=130 לכיסוי ערכים מעל 100)
];

// מחזיר את ה-label של bucket המתאים לאחוז חשיפה למניות נתון (6 טווחים)
export const getStockExposureBucketLabel = (percent: number | undefined): string => {
    if (percent == null) return "0%";

    // חיפוש הבאקט המתאים
    const bucket = stockExposureBuckets.find(b => percent >= b.min && percent < b.max);
    return bucket ? bucket.label : stockExposureBuckets[stockExposureBuckets.length - 1].label;
  };

// @deprecated - השתמש ב-getStockExposureBucketLabel במקום
export const getBucket = getStockExposureBucketLabel;

// טווחים וצבעים לחשיפה למניות (6 טווחים)
export const stockExposureRanges = ["0-10", "10-30", "30-60", "60-75", "75-90", "90-100"];
export const stockExposureColors: Record<string, string> = {
  "0-10": "#4CAF50",    // ירוק - חשיפה נמוכה
  "10-30": "#8BC34A",   // ירוק בהיר
  "30-60": "#FFC107",   // צהוב - חשיפה בינונית
  "60-75": "#FF9800",   // כתום
  "75-90": "#FF5722",   // כתום כהה
  "90-100": "#B71C1C",  // אדום כהה - חשיפה גבוהה מאוד
};

// פונקציה לקבלת טווח חשיפה למניות (6 טווחים)
export const getStockExposureRange = (exposurePercent: number): string => {
  if (exposurePercent < 10) return "0-10";
  if (exposurePercent < 30) return "10-30";
  if (exposurePercent < 60) return "30-60";
  if (exposurePercent < 75) return "60-75";
  if (exposurePercent < 90) return "75-90";
  return "90-100";
};

// buckets של חשיפה למניות המשמשים בפועל לחישוב האחוזון (percentile_6m_yield / percentile_12m_yield)
// בבקאנד (unified_fund_repository.py, _compute_percentiles) - BUCKETS = [0, 1, 30, 75, 201]
// השוני מ-stockExposureBuckets: אלו הם קבוצות ההשוואה שבתוכן מחושב הדירוג היחסי בין קרנות,
// ולא טווחי תצוגה לחשיפה עצמה - חשוב לא להתבלבל בין השניים
// max לא כלול בטווח (min <= value < max), תואם ל-pd.cut(..., right=False)
export const rankingStockExposureBuckets = [
  { label: "0", min: 0, max: 1 },        // 0%
  { label: "1-29", min: 1, max: 30 },    // 1-29%
  { label: "30-74", min: 30, max: 75 },  // 30-74%
  { label: "75-200", min: 75, max: 201 }, // 75-200%
  { label: "201+", min: 201, max: 9999 }, // 201%+ (לא ריאלי בפועל, נשמר לשלמות התאמה לבקאנד)
];

export const rankingStockExposureRanges = rankingStockExposureBuckets.map(b => b.label);

// צבעים לטווחי קבוצת הדירוג (בקטים בהם מחושב percentile_12m_yield / percentile_6m_yield)
export const rankingStockExposureColors: Record<string, string> = {
  "0": "#4CAF50",        // ירוק - ללא חשיפה למניות
  "1-29": "#8BC34A",     // ירוק בהיר
  "30-74": "#FFC107",    // צהוב - חשיפה בינונית
  "75-200": "#FF7043",   // כתום - חשיפה גבוהה
  "201+": "#B71C1C",     // אדום כהה (תיאורטי)
};

// שמות תצוגה ידידותיים לטווחי קבוצת הדירוג, לשימוש בלג'נדה/טולטיפ
// "75-200" ו-"201+" מוצגים תחת אותו שם ("מנייתי") מכיוון ששניהם מייצגים חשיפה מנייתית גבוהה מאוד
export const rankingStockExposureLabels: Record<string, string> = {
  "0": "אג\"ח",
  "1-29": "מוטה אג\"ח",
  "30-74": "כללי",
  "75-200": "מנייתי",
  "201+": "מנייתי",
};

// מחזיר את ה-label של קבוצת הדירוג המתאימה לאחוז חשיפה למניות נתון
// תואם בדיוק לחלוקה שמשמשת את הבקאנד לחישוב percentile_12m_yield / percentile_6m_yield
export const getRankingStockExposureBucketLabel = (percent: number | undefined | null): string => {
  const value = percent ?? 0;
  const bucket = rankingStockExposureBuckets.find(b => value >= b.min && value < b.max);
  return bucket ? bucket.label : rankingStockExposureBuckets[rankingStockExposureBuckets.length - 1].label;
};

export enum FundFieldSection {
  MOVING_YIELD = "חלון זמנים",
  COMMULATIVE_YIELD = "תשואה מצטברת",
  YIELD = "תשואה חודשית",
  DISTRIBUTION = "פיזור חשיפה",
  RANKING = "דירוג",
  QUALITY = "מדדי איכות ביצועים",
  OTHER = "אחר"
}

// 🟨 מיפוי שדות לסקשן
const sectionByField: Partial<Record<keyof FundDataBasic, FundFieldSection>> = {
  monthly_yield: FundFieldSection.YIELD,
  cumulative_yield_dynamic: FundFieldSection.COMMULATIVE_YIELD,
  year_to_date_yield: FundFieldSection.MOVING_YIELD,
  cumulative_12m_yield: FundFieldSection.MOVING_YIELD,
  cumulative_24m_yield: FundFieldSection.MOVING_YIELD,
  cumulative_6m_yield: FundFieldSection.MOVING_YIELD,
  yield_trailing_3_yrs: FundFieldSection.MOVING_YIELD,
  yield_trailing_5_yrs: FundFieldSection.MOVING_YIELD,
  avg_annual_yield_trailing_3yrs: FundFieldSection.MOVING_YIELD,
  avg_annual_yield_trailing_5yrs: FundFieldSection.MOVING_YIELD,

  foreign_exposure_percent: FundFieldSection.DISTRIBUTION,
  stock_market_percent: FundFieldSection.DISTRIBUTION,
  foreign_currency_exposure_percent: FundFieldSection.DISTRIBUTION,

  alpha: FundFieldSection.QUALITY,
  sharpe_ratio: FundFieldSection.QUALITY,
  standard_deviation: FundFieldSection.QUALITY,

  rank_yield_trailing_3_yrs: FundFieldSection.RANKING,
  rank_yield_trailing_5_yrs: FundFieldSection.RANKING,
  rank_cumulative_12m_yield: FundFieldSection.RANKING,
  rank_sharpe_ratio: FundFieldSection.RANKING,
  rank_alpha: FundFieldSection.RANKING,
  rank_standard_deviation: FundFieldSection.RANKING,
};

// 🟦 תרגום שדות לעברית
const labelByField: Partial<Record<keyof FundDataBasic, string>> = {
  uid: "מזהה פנימי",
  fund_id: "קוד מסלול",
  fund_name: "שם מסלול",
  fund_classification: "סיווג מסלול",
  controlling_corporation: "גוף שולט",
  managing_corporation: "גוף מנהל",
  report_period: "תקופת דיווח",
  inception_date: "תאריך הקמה",
  target_population: "אוכלוסיית יעד",
  specialization: "התמחות",
  sub_specialization: "תת־התמחות",

  deposits: "הפקדות",
  withdrawls: "משיכות",
  internal_transfers: "העברות פנימיות",
  net_monthly_deposits: "הפקדות חודשיות נטו",
  total_assets: "סה\"כ נכסים",

  avg_annual_management_fee: "דמי ניהול שנתיים ממוצעים",
  avg_deposit_fee: "דמי ניהול על הפקדות",

  monthly_yield: "תשואה חודשית",
  year_to_date_yield: "תשואה מתחילת שנה",
  yield_trailing_3_yrs: "תשואה מצטברת ב־3 שנים",
  yield_trailing_5_yrs: "תשואה מצטברת ב־5 שנים",
  avg_annual_yield_trailing_3yrs: "ממוצע תשואה שנתי ל־3 שנים",
  avg_annual_yield_trailing_5yrs: "ממוצע תשואה שנתי ל־5 שנים",
  cumulative_12m_yield: "תשואה מצטברת 12 חודשים",
  cumulative_6m_yield: "תשואה מצטברת 6 חודשים",
  cumulative_24m_yield: "תשואה מצטברת 24 חודשים",
  cumulative_yield_dynamic: "תשואה מצטברת",
  standard_deviation: "סטיית תקן",
  alpha: "אלפא",
  sharpe_ratio: "יחס שארפ",

  liquid_assets_percent: "נכסים נזילים (%)",
  stock_market_exposure: "חשיפה לשוק המניות",
  stock_market_percent: "חשיפה למניות (%)",
  foreign_exposure: "חשיפה לחו\"ל",
  foreign_currency_exposure: "חשיפה למט\"ח",
  foreign_exposure_percent: "חשיפה לחו\"ל (%)",
  foreign_currency_exposure_percent: "חשיפה למט\"ח (%)",

  managing_corporation_legal_id: "קוד גוף מנהל",
  current_date: "תאריך נוכחי",

  id: "מזהה רשומה",
  file_type: "מקור קובץ",
  year: "שנה",
  actuarial_adjustment: "התאמה אקטוארית",
  product_type: "סוג מוצר",

  rank_yield_trailing_3_yrs: "דירוג תשואה 3 שנים",
  rank_yield_trailing_5_yrs: "דירוג תשואה 5 שנים",
  rank_cumulative_12m_yield: "דירוג תשואה מצטברת",
  rank_sharpe_ratio: "דירוג שארפ",
  rank_alpha: "דירוג אלפא",
  rank_standard_deviation: "דירוג סטיית תקן",
};

export const formatTargetPopulation = (value?: TargetPopulation | null): string => {
  switch (value) {
    case "1":
      return "עובדי גוף מסויים";
    case "2":
      return "מגזר מסויים";
    default:
      return "כלל האוכלוסיה";
  }
};
export const formatFundFieldLabel = (field: keyof FundDataBasic): FormattedField => {
  return {
    section: sectionByField[field] ?? FundFieldSection.OTHER,
    label: labelByField[field] ?? field,
  };
};
export const getPercentileBucket = (percentile: number): string => {
    if (percentile <= 20) return "0-20";
    if (percentile <= 40) return "21-40";
    if (percentile <= 60) return "41-60";
    if (percentile <= 80) return "61-80";
    if (percentile <= 90) return "81-90";
    return "91-100";
  };

  // חדש: רשימת כל 6 buckets של אחוזון תשואה
export  const allPercentileBuckets = ["0-20", "21-40", "41-60", "61-80", "81-90", "91-100"];

  // תווית לכל bucket של אחוזון
export  const percentileBucketLabels: Record<string, string> = {
    "0-20": "הגרועות ביותר (0-20%)",
    "21-40": "מתחת לממוצע (21-40%)",
    "41-60": "ממוצע (41-60%)",
    "61-80": "מעל הממוצע (61-80%)",
    "81-90": "טובות מאוד (81-90%)",
    "91-100": "הטובות ביותר (91-100%)",
  };

// טווחים וצבעים לחשיפה לחו"ל
export const foreignExposureRanges = ["0-25", "26-50", "51-75", "76-100"];
export const foreignExposureColors: Record<string, string> = {
  "0-25": "#4CAF50",
  "26-50": "#FF9800",
  "51-75": "#2196F3",
  "76-100": "#F44336",
};

// טווחים וצבעים לחשיפה למט"ח
export const currencyExposureRanges = ["0-25", "26-50", "51-75", "76-100"];
export const currencyExposureColors: Record<string, string> = {
  "0-25": "#4CAF50",
  "26-50": "#FF9800",
  "51-75": "#2196F3",
  "76-100": "#F44336",
};

// צבעים כלליים לחשיפה (לתאימות לאחור - משתמש בטווחי חו"ל/מט"ח)
export const exposureRangeColors = foreignExposureColors;

// רשימת כל טווחי החשיפה (לתאימות לאחור - משתמש בטווחי חו"ל/מט"ח)
export const allExposureRanges = foreignExposureRanges;

// צבעי palette לתת-התמחויות (sub_specialization)
export const subspecializationColors = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
  '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
  '#10AC84', '#EE5A24', '#0652DD', '#9C88FF', '#FFC312',
  '#C4E538', '#12CBC4', '#FDA7DF', '#ED4C67', '#F79F1F'
];

/**
 * מחזיר צבע עבור תת-התמחות על בסיס hash של השם
 * מבטיח שאותו שם תמיד יקבל אותו צבע עם גוון ושקיפות
 */
export const getSubspecializationColor = (subspecialization: string): string => {
  if (!subspecialization || subspecialization === '-') {
    return 'rgba(189, 189, 189, 0.7)'; // צבע אפור לערכים ריקים
  }

  // חישוב hash מהמחרוזת
  const hash = subspecialization.split('').reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);

  // יצירת גוון RGB על בסיס ה-hash
  const r = 100 + (hash * 17) % 100;
  const g = 100 + (hash * 31) % 100;
  const b = 100 + (hash * 47) % 100;

  return `rgba(${r}, ${g}, ${b}, 0.7)`;
};

// צבעים לסיווג קרנות פנסיה (fund_classification)
export const fundClassificationColors: Record<string, string> = {
  "פנסיה מקיפה": "#AB47BC",
  "פנסיה משלימה": "#42A5F5",
  "כללי": "#66BB6A",
  "-": "#BDBDBD",
};
export const getColorForPercentileNumber = (percentile: number): string => {
  if (percentile <= 20) return "#F44336";   // 0-20: אדום
  if (percentile <= 40) return "#FF9800";   // 21-40: כתום
  if (percentile <= 60) return "#FFC107";   // 41-60: צהוב
  if (percentile <= 80) return "#8BC34A";   // 61-80: ירוק בהיר
  if (percentile <= 90) return "#4CAF50";   // 81-90: ירוק
  return "#2E7D32";                         // 91-100: ירוק כהה
};
  // חדש: צבעים ל-6 buckets של אחוזון תשואה (מגרוע לטוב)
export const percentileBucketColors: Record<string, string> = {
  "0-20": getColorForPercentileNumber(10),
  "21-40": getColorForPercentileNumber(30),
  "41-60": getColorForPercentileNumber(50),
  "61-80": getColorForPercentileNumber(70),
  "81-90": getColorForPercentileNumber(85),
  "91-100": getColorForPercentileNumber(95),
};

const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace("#", "");
  return [
    parseInt(clean.substring(0, 2), 16),
    parseInt(clean.substring(2, 4), 16),
    parseInt(clean.substring(4, 6), 16),
  ];
};

// עוגנים לאינטרפולציה רציפה, נגזרים מאותם צבעים המשמשים בכל האפליקציה לדירוג אחוזון
const continuousPercentileColorStops: { stop: number; color: [number, number, number] }[] = [
  { stop: 0, color: hexToRgb(getColorForPercentileNumber(0)) },
  { stop: 25, color: hexToRgb(getColorForPercentileNumber(25)) },
  { stop: 50, color: hexToRgb(getColorForPercentileNumber(50)) },
  { stop: 75, color: hexToRgb(getColorForPercentileNumber(75)) },
  { stop: 100, color: hexToRgb(getColorForPercentileNumber(100)) },
];

// כמו getColorForPercentileNumber, אך עם אינטרפולציה רציפה בין הצבעים במקום 6 buckets בדידים
export const getContinuousPercentileColor = (percentile: number): string => {
  const value = Math.max(0, Math.min(100, percentile));

  let lower = continuousPercentileColorStops[0];
  let upper = continuousPercentileColorStops[continuousPercentileColorStops.length - 1];
  for (let i = 0; i < continuousPercentileColorStops.length - 1; i++) {
    if (value >= continuousPercentileColorStops[i].stop && value <= continuousPercentileColorStops[i + 1].stop) {
      lower = continuousPercentileColorStops[i];
      upper = continuousPercentileColorStops[i + 1];
      break;
    }
  }

  const range = upper.stop - lower.stop;
  const ratio = range === 0 ? 0 : (value - lower.stop) / range;

  const r = Math.round(lower.color[0] + (upper.color[0] - lower.color[0]) * ratio);
  const g = Math.round(lower.color[1] + (upper.color[1] - lower.color[1]) * ratio);
  const b = Math.round(lower.color[2] + (upper.color[2] - lower.color[2]) * ratio);

  return `rgb(${r}, ${g}, ${b})`;
};

// פונקציה לקבלת טווח חשיפה לחו"ל
export const getForeignExposureRange = (exposurePercent: number): string => {
  if (exposurePercent <= 25) return "0-25";
  if (exposurePercent <= 50) return "26-50";
  if (exposurePercent <= 75) return "51-75";
  return "76-100";
};

// פונקציה לקבלת טווח חשיפה למט"ח
export const getCurrencyExposureRange = (exposurePercent: number): string => {
  if (exposurePercent <= 25) return "0-25";
  if (exposurePercent <= 50) return "26-50";
  if (exposurePercent <= 75) return "51-75";
  return "76-100";
};

// רשימת כל השדות המספריים הזמינים
export const numericFields: (keyof FundDataBasic)[] = [
  "cumulative_yield_dynamic",
  "monthly_yield",
  "year_to_date_yield",
  "yield_trailing_3_yrs",
  "yield_trailing_5_yrs",
  "cumulative_6m_yield",
  "cumulative_12m_yield",
  "cumulative_24m_yield",
  "standard_deviation",
  "alpha",
  "sharpe_ratio",
  "stock_market_percent",
  "foreign_currency_exposure",
  "foreign_exposure_percent",
  "foreign_currency_exposure_percent",
  "rank_yield_trailing_3_yrs",
  "rank_yield_trailing_5_yrs",
  "rank_cumulative_12m_yield",
  "rank_sharpe_ratio",
  "rank_alpha",
  "rank_standard_deviation",
  "avg_annual_management_fee",
  "avg_deposit_fee",
  "foreign_exposure",
  "stock_market_exposure",
  "deposits",
  "withdrawls",
  "internal_transfers",
  "net_monthly_deposits",
  "total_assets",
  "avg_annual_yield_trailing_3yrs",
  "avg_annual_yield_trailing_5yrs",
  "actuarial_adjustment",
  "liquid_assets_percent",
];

export const groupNumericFieldsBySection = (
  fields: (keyof FundDataBasic)[]
): Record<string, { field: keyof FundDataBasic; label: string }[]> => {
  const grouped: Record<string, { field: keyof FundDataBasic; label: string }[]> = {};

  fields.forEach((field) => {
    const { section, label } = formatFundFieldLabel(field);
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push({ field, label });
  });

  return grouped;
};

interface FormattedField {
  label: string;
  section: FundFieldSection;
}

// 🔍 הסברים מפורטים לשדות נבחרים
const fieldExplanations: Partial<Record<keyof FundDataBasic, string>> = {
  total_assets: "נכסי הקרן – סכום כולל במיליוני ₪",

  alpha: `אלפא (α) במסלול של קרן השתלמות היא מדד שמראה כמה מנהל ההשקעות הצליח "להכות את השוק" יחסית לרמת הסיכון שהוא לקח.

זו הדרך למדוד אם הניהול האקטיבי באמת מוסיף ערך — או לא.

אלפא = התשואה העודפת של הקרן מעבר למה שהיה צפוי לפי רמת הסיכון שלה.

• אם הקרן הביאה תוצאה טובה יותר מהמדד הסביר → האלפא חיובית
• אם הקרן פיגרה מהמדד או הניבה פחות ממה שצפוי → האלפא שלילית`,

  sharpe_ratio: `כמה תשואה הקרן השיגה מעבר לסיכון שהיא לקחה.
כמה הקרן משלמת לך על כל "יחידת סיכון" שהיא לוקחת.

📌 פירוש ערכי שארפ:
• > 1.0 - מצוין: הקרן נותנת הרבה תשואה ביחס לסיכון
• 0.5–1.0 - טוב: הקרן יעילה אך לא מושלמת
• 0.0–0.5 - חלש: הסיכון גבוה ביחס לתשואה
• < 0.0 - רע: הקרן מפסידה או מתגמלת פחות מהריבית חסרת הסיכון

🧠 למה זה חשוב?
כי הרבה מסלולים מראים תשואה גבוהה, אבל:
• אם הם לקחו הרבה סיכון → זה לא נחשב להצלחה
• שארפ מנקה את השפעת התנודתיות
• ומראה מי באמת מנהל את הכסף ביעילות

לכן, הוא מדד קריטי להשוואת מסלולים אקטיביים.`,

  standard_deviation: `סטיית תקן = כמה הקרן תנודתית.

• סטיית תקן גבוהה → הקרן עולה ויורדת חזק → מסוכן יותר
• סטיית תקן נמוכה → הקרן יציבה יחסית → מסוכן פחות

🧠 למה משתמשים בסטיית תקן?
כדי למדוד "כמה לחץ" יש בהשקעה:
• האם היא עולה ויורדת בחדות?
• או שהיא מתנהגת חלק ויציב לאורך זמן?

זו דרך להעריך סיכון בלי להסתכל רק על תשואה.`,
};

/**
 * מחזיר הסבר מפורט לשדה אם קיים
 * @param field שם השדה
 * @returns הסבר או undefined אם אין
 */
export const getFieldExplanation = (field: keyof FundDataBasic): string | undefined => {
  return fieldExplanations[field];
};
